import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";
import { FaInstagram, FaFacebookF } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const Layout = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, [setIsScrolled]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location, setMobileMenuOpen]);

  const getNavLinkClass = (link, isMobile = false) => {
    const base = isMobile
      ? "font-body text-sm tracking-[0.15em] uppercase py-2 transition-colors duration-300"
      : "font-body text-xs tracking-[0.15em] uppercase transition-colors duration-300";
    if (link.highlight) return `${base} text-bronze${isMobile ? "" : " hover:text-bronze-light"}`;
    if (location.pathname === link.path) return `${base} text-bronze`;
    return `${base} text-stone-50/80${isMobile ? "" : " hover:text-stone-50"}`;
  };

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Our Process", path: "/our-process" },
    { name: "Portfolio", path: "/portfolio" },
    { name: "Contact", path: "/contact", highlight: true },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? "bg-charcoal/98 backdrop-blur-md" : "bg-charcoal"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link
              to="/"
              data-testid="logo-link"
              className="flex items-center"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_3e849b0a-3eb9-4a57-9473-38116027ffed/artifacts/6iitq234_33233402-ca77-44e6-9ac5-76afe5ec4b84.png" 
                alt="PJMW Services - Luxury Bathrooms & Renovations"
                className="h-16 md:h-20 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={getNavLinkClass(link)}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-button"
              className="md:hidden text-stone-50 p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-charcoal border-t border-stone-50/10"
              data-testid="mobile-menu"
            >
              <nav className="px-6 py-6 flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                    className={getNavLinkClass(link, true)}
                  >
                    {link.name}
                  </Link>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-grow pt-20">{children}</main>

      {/* Footer */}
      <footer data-testid="main-footer" className="bg-charcoal text-stone-50/70 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {/* Brand */}
            <div>
              <h3 className="font-heading text-xl text-stone-50 mb-4">
                PJMW Services
              </h3>
              <p className="font-body text-sm leading-relaxed text-stone-50/60">
                Luxury Bathrooms & Refurbishment
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-body text-xs tracking-[0.15em] uppercase text-stone-50 mb-4">
                Quick Links
              </h4>
              <nav className="flex flex-col gap-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="font-body text-sm text-stone-50/60 hover:text-bronze transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Contact Details */}
            <div>
              <h4 className="font-body text-xs tracking-[0.15em] uppercase text-stone-50 mb-4">
                Contact
              </h4>
              <div className="flex flex-col gap-3">
                <a
                  href="tel:+4407985483822"
                  className="font-body text-sm text-stone-50/60 hover:text-bronze transition-colors duration-300 flex items-center gap-2"
                >
                  <Phone size={14} strokeWidth={1.5} />
                  +44 07985 483822
                </a>
                <a
                  href="mailto:info@PJMWServices.co.uk"
                  className="font-body text-sm text-stone-50/60 hover:text-bronze transition-colors duration-300 flex items-center gap-2"
                >
                  <Mail size={14} strokeWidth={1.5} />
                  info@PJMWServices.co.uk
                </a>
                <p className="font-body text-sm text-stone-50/60 flex items-center gap-2">
                  <MapPin size={14} strokeWidth={1.5} />
                  Northamptonshire
                </p>
                <div className="flex items-center gap-4 mt-3">
                  <a
                    href="https://www.instagram.com/pjmwservices/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-stone-50/50 hover:text-bronze transition-colors duration-300"
                    data-testid="social-instagram"
                    aria-label="Instagram"
                  >
                    <FaInstagram size={18} />
                  </a>
                  <a
                    href="https://www.facebook.com/profile.php?id=61590735900118"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-stone-50/50 hover:text-bronze transition-colors duration-300"
                    data-testid="social-facebook"
                    aria-label="Facebook"
                  >
                    <FaFacebookF size={18} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-stone-50/10">
            <p className="font-body text-xs text-stone-50/40 text-center">
              PJMW Services Limited. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
